import type { GetFinalizedStoryResponse, GetStoryResponse } from "@api/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Printer, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigation } from "../contexts/NavigationContext";
import { useToast } from "../hooks/useToast";
import { useUserAccount } from "../hooks/useUserAccount";
import { apiClient } from "../lib/api";
import { PageLayout } from "./PageLayout";
import { ShareButton } from "./ShareButton";
import { StoryViewer } from "./StoryViewer";
import { Button } from "./ui/button";
import { CenteredCard } from "./ui/centered-card";
import { ConfirmationModal } from "./ui/confirmation-modal";

interface StoryViewerPageProps {
    storyData: GetStoryResponse | GetFinalizedStoryResponse;
}

function isStoryFinalized(storyData: { story?: { status?: string } }): storyData is GetFinalizedStoryResponse {
    return storyData?.story?.status === "finalized";
}

function PrintButton({ storyId }: { storyId: string; storyTitle: string }) {
    const { tier } = useUserAccount();
    const isPremium = tier === "plus" || tier === "pro";
    const { toast } = useToast();

    // Future state: support disabled button when Premium launches
    if (!isPremium) {
        return null;
    }

    const handlePrint = () => {
        if (!isPremium) {
            toast({
                title: "Plus Feature",
                description: "Print Story is available for premium users. Upgrade to access this feature.",
                variant: "default",
            });
            return;
        }

        // Open print view in new tab
        const printUrl = `/story/${storyId}/print`;
        window.open(printUrl, "_blank");
    };

    return (
        <Button
            onClick={handlePrint}
            variant={isPremium ? "outline" : "ghost"}
            size="lg"
            className="flex items-center gap-2"
            disabled={!isPremium}
        >
            <Printer className="w-4 h-4" />
            {isPremium ? "Print Story" : "Print Story (Plus)"}
        </Button>
    );
}

function CompletedStoryView({
    storyData,
    onCreateNewStory,
}: {
    storyData: GetFinalizedStoryResponse;
    onCreateNewStory: () => void;
}) {
    return (
        <div className="space-y-8 pb-4 sm:pb-8">
            {/* Shared story viewer */}
            <StoryViewer storyData={storyData} />

            {/* Post-story actions */}
            <div className="text-center space-y-6">
                <p className="text-lg text-secondary font-medium">Ready to create another adventure?</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <PrintButton
                        storyId={storyData.story.id}
                        storyTitle={storyData.story.title || storyData.story.topic}
                    />
                    <ShareButton
                        storyId={storyData.story.id}
                        storyTitle={storyData.story.title || storyData.story.topic}
                    />
                    <Button onClick={onCreateNewStory} size="lg">
                        Create New Story
                    </Button>
                </div>
            </div>
        </div>
    );
}

export function StoryViewerPage({ storyData }: StoryViewerPageProps) {
    const { navigateToMyStories, navigateToStoryMode } = useNavigation();
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const queryClient = useQueryClient();

    const deleteStoryMutation = useMutation({
        mutationFn: () => {
            if (!storyData?.story?.id) {
                throw new Error("Missing story data");
            }
            return apiClient.deleteStory(storyData.story.id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["user-stories-paginated"] });
            navigateToMyStories();
        },
        onError: () => {
            alert("Failed to delete story. Please try again.");
        },
    });

    const isFinalized = storyData ? isStoryFinalized(storyData) : false;
    const story = storyData?.story;

    const handleDeleteClick = () => setDeleteModalOpen(true);
    const handleDeleteConfirm = () => deleteStoryMutation.mutate();
    const handleDeleteCancel = () => setDeleteModalOpen(false);

    // If story is not finalized, show message
    if (!isFinalized) {
        return (
            <CenteredCard>
                <div className="text-center space-y-4">
                    <BookOpen className="h-12 w-12 text-primary mx-auto" />
                    <h2 className="text-2xl font-bold">Story In Progress</h2>
                    <p className="text-muted-foreground">This story is not finished yet. It should be in edit mode.</p>
                </div>
            </CenteredCard>
        );
    }

    const pageTitle = "Story";

    return (
        <PageLayout
            title={pageTitle}
            breadcrumbs={[{ label: "Home", to: "/" }, { label: "My Stories", to: "/my-stories" }, { label: "Story" }]}
        >
            <main className="container mx-auto px-4 sm:px-6 relative">
                <div className="max-w-4xl mx-auto">
                    {/* Absolute positioned delete button */}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDeleteClick}
                        className="absolute top-[52px] sm:top-0 right-2 sm:right-6 h-8 w-8 p-0 text-destructive hover:text-destructive/80 hover:bg-destructive/10 dark:hover:bg-destructive/20"
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>

                    <div className="mb-6">
                        <h1 className="text-2xl sm:text-3xl font-bold text-primary text-center">
                            {story?.title || story?.topic || "Your Story"}
                        </h1>
                    </div>

                    <div className="space-y-8">
                        <CompletedStoryView storyData={storyData} onCreateNewStory={navigateToStoryMode} />
                    </div>
                </div>
            </main>

            <ConfirmationModal
                isOpen={deleteModalOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title="Delete Story"
                description={`Are you sure you want to delete "${story?.topic}"? This will permanently remove all your progress and drawings. This action cannot be undone.`}
                variant="destructive"
                confirmButtonText="Delete"
                isLoading={deleteStoryMutation.isPending}
                loadingText="Deleting..."
            />
        </PageLayout>
    );
}
